import { useState } from "react";
import { X, Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function VinDialog({ onClose, onApply }) {
  const [vin, setVin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const decode = async (e) => {
    e?.preventDefault();
    if (vin.trim().length < 8) {
      toast.error("VIN should be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vin/decode", { vin: vin.trim() });
      setResult(data);
      if (data.error && !data.make) {
        toast.error(data.error.split(";")[0]);
      } else if (data.make) {
        toast.success(`Decoded: ${data.make} ${data.model || ""}`);
      }
    } catch (err) {
      toast.error("VIN decode failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vin-dialog" className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-neutral-50 border border-neutral-200 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono-tech text-[#FF5100]">
              VIN DECODER
            </div>
            <h2 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <Hash size={20} /> Decode VIN
            </h2>
          </div>
          <button data-testid="vin-close-button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={decode} className="space-y-3">
          <input
            data-testid="vin-input"
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            maxLength={17}
            placeholder="e.g. 1HGCM82633A004352"
            className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm font-mono-tech tracking-wider"
          />
          <button
            type="submit"
            data-testid="vin-decode-button"
            disabled={busy}
            className="w-full px-6 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors flex items-center justify-center gap-2"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Decoding...</> : "Decode"}
          </button>
        </form>

        {result && result.make && (
          <div data-testid="vin-result" className="mt-4 border border-neutral-200 bg-white p-3 space-y-1.5">
            <Row label="Make" value={result.make} />
            <Row label="Model" value={result.model} />
            <Row label="Year" value={result.year} />
            <Row label="Engine" value={result.engine} />
            <Row label="Fuel" value={result.fuel_type} />
            <Row label="Body" value={result.body_class} />
            <button
              data-testid="vin-apply-button"
              onClick={() => onApply(result)}
              className="mt-3 w-full px-6 h-10 bg-[#FF5100] hover:bg-[#E64A00] text-white font-bold uppercase tracking-wider text-xs transition-colors"
            >
              Use in chat
            </button>
          </div>
        )}

        {result && !result.make && (
          <div className="mt-4 border border-amber-700/50 bg-amber-900/10 p-3 text-xs text-amber-400">
            Couldn't decode this VIN with NHTSA. It might be a non-US-registered vehicle.
            You can still type the make/model into the chat directly.
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono-tech text-neutral-500">
        {label}
      </div>
      <div className="font-mono-tech text-neutral-900 truncate">{value || "—"}</div>
    </div>
  );
}
